import { pageQueryParams } from '@/components/search/pageQueryParams'
import { SortableHeader } from '@/components/search/SortableHeader'
import './Activate.module.css'

import React, { ChangeEventHandler } from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { Services } from '@/services/services'
import { useNavigate, useParams } from 'react-router-dom'

import { Machine } from '@/services/model/machine'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { ActivateQRCodeForm, ActivateQRCodeFormData } from '@/components/forms/qrcode/Activate'
import { useForm } from 'react-hook-form'
import { useModal } from '@/components/modal/Modal'
import { CreateMachineWizardPopup, MachineCreateWizard } from '@/popups/machines/wizard/create/Main'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { SimpleMachine } from '../communal/my/MyMachines'
import { useAppContext } from '@/context/app'
import { Spinner } from '@/components/spinner'
import { Roles, isAdminRole } from '@/services/repository/users'
import { ActivateMapComponent } from './activate/Map'

var roles = [Roles.CompanyAdmin, Roles.SuperAdmin, Roles.HyperAdmin, Roles.CompanyManager]

interface Dict {
  id: string
  label: string
}

export const QRActivate = () => {
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const qrCodeId = useParams().id
  const searchTableRef = useRef<SearchTableRef>(null)
  const allowedUser = useMemo(() => roles.includes(session?.user.role || '' as any), [])

  if (!translates || !session) {
    return <Spinner />
  }

  const addQRCode = async (machineId: string) => {
    services.session.fetchWithToken('/api/communal/qr/activate', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: qrCodeId,
        machineId
      })
    }).then(() => {
      navigate(`/qr/tag/${qrCodeId}`)
    });
  }

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      const url = isAdminRole(session.user.role) ?
        `/api/superadmin/machines/search?${pageQueryParams(q)}`
        : `/api/communal/my/machines/search?${pageQueryParams(q)}`
      return services.session.fetchWithToken(url).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 6,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => MachineCreateWizard({
        ...props, qrCode: qrCodeId, onDone: (machineId: string) => {
          navigate(`/communal/machine/${machineId}`)
          props.onClose()
        }
      }))}>
        <i className='svgfont-add' />
      </div>
    </>,
    defaultSortBy: 'manufacturer',
    header: (query, onSort) => <>
      <div className='hcol'></div>
      <SortableHeader field='manufacturer' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.manufacturer')}</SortableHeader>
      <SortableHeader field='model' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.model')}</SortableHeader>
      <SortableHeader field='tagNumber' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.number')}</SortableHeader>
    </>,
    row: (row) => <>
      <div className='col icocol icocol-active' onClick={() => addQRCode(row.id)}>
        <i className='svgfont-add' />
      </div>
      <div className='col' onClick={() => addQRCode(row.id)}>{row.manufacturer}</div>
      <div className='col' onClick={() => addQRCode(row.id)}>{row.model}</div>
      <div className='col' onClick={() => addQRCode(row.id)}>{row.internalNumber || row.imei || row.id}</div>
    </>,
    translates: session.translates
  }

  if (!allowedUser) {
    return <LoggedInPageLayout services={services} session={session} pageName='activate-tag'>
      <h3>{translates.for('page.qr_activate.title')}</h3>
      <div className='permission-denide'>{translates.for('errors.no_permissions')}</div>
    </LoggedInPageLayout >
  }

  return <LoggedInPageLayout services={services} session={session} pageName='activate-tag'>
    <h3 className='section'>{translates.for('page.qr_activate.title')}</h3>
    <p className='section'>{translates.for('page.qr_activate.help')}</p>

    <a className='main-btn' onClick={() => openModal((props) => CreateMachineWizardPopup({
      ...props, qrCode: qrCodeId, onDone: (machineId: string) => {
        navigate(`/communal/machine/${machineId}`)
        props.onClose()
      }
    }))}>
      {translates.for('page.qr_activate.add_btn')}
    </a>

    <h3 className='section'>{translates.for('page.qr_activate.find_section_title')}</h3>
    <div style={{
      height: "min(350px, 80vh)"
    }}>
      <ActivateMapComponent fetchUrl={isAdminRole(session.user.role) ? `/api/communal/map` : `/api/communal/my/map`} onSelect={(machine: Machine) => {
        if (machine.id) {
          addQRCode(machine.id)
          return
        }
        openModal((props) => CreateMachineWizardPopup({
          ...props, qrCode: qrCodeId, machine, onDone: (machineId: string) => {
            navigate(`/communal/machine/${machineId}`)
            props.onClose()
          }
        }))
      }} />
    </div>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout >
}
